import type React from "react";
import {useState} from "react";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    type Theme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "~/icons/svg/CloseIcon.svg?react";
import {useStores} from "~/stores";
import type {
    ComplianceArtifactKind,
    ComplianceRequirement,
} from "~/pages/supplier-360/types/Supplier360Types";
import {COMPLIANCE_ARTIFACT_KINDS} from "~/pages/supplier-360/types/Supplier360Types";
import {ARTIFACT_KIND_LABEL} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";
import {useCreateComplianceArtifactMutation} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useCreateComplianceArtifactMutation";
import {EvidenceDropZone} from "~/pages/supplier-360/tabs/SupplierCompliance/components/recordDetail/EvidenceDropZone";

type UploadEvidenceFormProps = {
    record: ComplianceRequirement;
    /** The BU's name for a business-unit record, for the scope note. */
    businessUnit: string | undefined;
    onClose: () => void;
};

type FormValues = {
    kind: ComplianceArtifactKind;
    issuedAt: string;
    expiresAt: string;
};

/** A `Date` as the user's local calendar day, `YYYY-MM-DD` — what a `type="date"` input holds. */
function toLocalIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The default validity: from today, until a year from today minus a day. */
function defaultValidity(): Pick<FormValues, 'issuedAt' | 'expiresAt'> {
    const today = new Date();
    const until = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate() - 1);
    return {issuedAt: toLocalIsoDate(today), expiresAt: toLocalIsoDate(until)};
}

/**
 * "Upload evidence" (`SxcEvidenceModal`). The file — dropped or clicked into `EvidenceDropZone` —
 * is required and sent as `multipart/form-data` (§2.9); its own file name is the evidence `name`.
 * Validity defaults to one year from today. `uploaded_by` is the signed-in user. Creating the
 * evidence flips the record to `met` server-side.
 */
export const UploadEvidenceForm: React.FC<UploadEvidenceFormProps> = ({record, businessUnit, onClose}) => {
    // Full screen on phones, so the action buttons stay in reach.
    const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const {authStore} = useStores();
    const [values, setValues] = useState<FormValues>(() => ({
        kind: COMPLIANCE_ARTIFACT_KINDS[0],
        ...defaultValidity(),
    }));
    const [file, setFile] = useState<File | null>(null);
    const createMutation = useCreateComplianceArtifactMutation();
    const isPending = createMutation.isPending;
    const isValid = file !== null;

    const set = (key: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) =>
        setValues(previous => ({...previous, [key]: event.target.value}));

    const submit = () => {
        if (!isValid || isPending || file === null) return;
        createMutation.mutate({
            entity: record.entity,
            satisfies_requirement: record.id,
            kind: values.kind,
            name: file.name,
            uploaded_by: authStore.displayUser,
            issued_at: values.issuedAt || null,
            expires_at: values.expiresAt || null,
            file,
            file_size: file.size,
        }, {onSuccess: onClose});
    };

    const closeIfIdle = () => {
        if (!isPending) onClose();
    };

    return (
        <Dialog fullScreen={fullScreen} open onClose={closeIfIdle} fullWidth maxWidth="sm" aria-labelledby="upload-evidence-title">
            <DialogTitle id="upload-evidence-title" sx={{pr: 6, fontSize: 16}}>
                {`Upload evidence · ${record.name}`}
                <IconButton
                    onClick={closeIfIdle}
                    title="Close"
                    size="small"
                    sx={{position: 'absolute', top: 12, right: 12}}
                >
                    <CloseIcon width={16} height={16}/>
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <EvidenceDropZone file={file} onFile={setFile} disabled={isPending}/>

                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField select label="Evidence type" value={values.kind} size="small" fullWidth onChange={set('kind')}>
                            {COMPLIANCE_ARTIFACT_KINDS.map(kind => (
                                <MenuItem key={kind} value={kind}>{ARTIFACT_KIND_LABEL[kind]}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Satisfies record"
                            value={record.name}
                            size="small"
                            fullWidth
                            slotProps={{htmlInput: {readOnly: true}}}
                        />
                    </Stack>

                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField
                            label="Valid from"
                            type="date"
                            value={values.issuedAt}
                            size="small"
                            fullWidth
                            slotProps={{inputLabel: {shrink: true}}}
                            onChange={set('issuedAt')}
                        />
                        <TextField
                            label="Valid until"
                            type="date"
                            value={values.expiresAt}
                            size="small"
                            fullWidth
                            slotProps={{inputLabel: {shrink: true}}}
                            onChange={set('expiresAt')}
                        />
                    </Stack>

                    <Alert severity="info" variant="outlined">
                        {record.layer === 'company_wide'
                            ? 'Company-wide evidence is uploaded once and satisfies this record for every business unit.'
                            : `Uploads stay scoped to ${businessUnit ?? 'this business unit'}. Company-wide documents are uploaded once in the company layer.`}
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button variant="outlined" disabled={isPending} onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={isPending || !isValid}
                    endIcon={isPending ? <CircularProgress size={16} color="inherit"/> : undefined}
                    onClick={submit}
                    sx={{textTransform: 'none'}}
                >
                    Upload evidence
                </Button>
            </DialogActions>
        </Dialog>
    );
};
